import { useCallback, useEffect, useState } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import {
  isAlertMetaHealth,
  WHATSAPP_ACCOUNTS_UPDATED_EVENT,
} from '@/lib/metaAccountHealth';
import {
  DEFAULT_SESSION_RESOURCE_TTL_MS,
  getCachedAuthenticatedResource,
  getOrFetchAuthenticatedResource,
  invalidateAuthenticatedResource,
  isAuthenticatedResourceFresh,
  subscribeAuthenticatedResource,
} from '@/lib/authenticatedResource';

export interface ConnectedAccountHealth {
  id: string;
  phone_number?: string;
  display_name?: string;
  status?: string;
  meta_health_status?: string | null;
}

interface UseMetaAccountHealthResult {
  accounts: ConnectedAccountHealth[];
  hasCriticalMetaHealth: boolean;
  criticalAccounts: ConnectedAccountHealth[];
  loading: boolean;
  refresh: () => Promise<void>;
}

const RESOURCE_KEY = 'whatsapp-connected-accounts' as const;

async function fetchConnectedAccountsFromApi(): Promise<ConnectedAccountHealth[]> {
  const response = await api.get('/whatsapp/connected-accounts');
  const list = response.data?.accounts || [];
  return Array.isArray(list) ? list : [];
}

/** Shared fetch used by hooks and one-off checks (e.g. create campaign). */
export async function fetchConnectedAccounts(
  options?: { force?: boolean }
): Promise<ConnectedAccountHealth[]> {
  return getOrFetchAuthenticatedResource(
    RESOURCE_KEY,
    fetchConnectedAccountsFromApi,
    { force: options?.force }
  );
}

export function hasActiveWhatsAppAccount(
  accounts: ConnectedAccountHealth[]
): boolean {
  return accounts.some((account) => account.status === 'active');
}

/**
 * Loads WhatsApp connected accounts and derives alert health
 * (restricted / blocked only — limited does not show UI alerts).
 * Multiple hook instances share one in-flight / TTL-cached request.
 */
export function useMetaAccountHealth(): UseMetaAccountHealthResult {
  const { isAuthenticated } = useAuth();
  const [accounts, setAccounts] = useState<ConnectedAccountHealth[]>(
    () => getCachedAuthenticatedResource<ConnectedAccountHealth[]>(RESOURCE_KEY) ?? []
  );
  const [loading, setLoading] = useState(
    () => !getCachedAuthenticatedResource<ConnectedAccountHealth[]>(RESOURCE_KEY)
  );

  const refresh = useCallback(async (force = false) => {
    try {
      const list = await fetchConnectedAccounts({ force });
      setAccounts(list);
    } catch {
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubscribe = subscribeAuthenticatedResource<ConnectedAccountHealth[]>(
      RESOURCE_KEY,
      (data) => {
        if (data !== undefined) setAccounts(data);
      }
    );

    refresh(false);

    const onFocus = () => {
      // Skip network if session cache is still fresh (avoids Alt-Tab storms).
      if (isAuthenticatedResourceFresh(RESOURCE_KEY, DEFAULT_SESSION_RESOURCE_TTL_MS)) {
        return;
      }
      // Not forced: every mounted instance joins the same in-flight request.
      refresh(false);
    };

    const onAccountsUpdated = () => {
      invalidateAuthenticatedResource(RESOURCE_KEY);
      refresh(true);
    };

    window.addEventListener('focus', onFocus);
    window.addEventListener(WHATSAPP_ACCOUNTS_UPDATED_EVENT, onAccountsUpdated);
    return () => {
      unsubscribe();
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(WHATSAPP_ACCOUNTS_UPDATED_EVENT, onAccountsUpdated);
    };
  }, [isAuthenticated, refresh]);

  const criticalAccounts = accounts.filter(
    (a) => a.status === 'active' && isAlertMetaHealth(a.meta_health_status)
  );

  return {
    accounts,
    hasCriticalMetaHealth: criticalAccounts.length > 0,
    criticalAccounts,
    loading,
    refresh: () => refresh(true),
  };
}
